/**
 * Utility functions for timezone conversions using native Intl API.
 */

// Returns the detected local timezone of the browser
export function getLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

// Returns the organization's timezone, falling back to browser timezone
export function getOrgTimezone(organization) {
  return (organization && organization.timezone) || getLocalTimezone();
}

// Formats an ISO date string into a localized string for the specified timezone
export function formatDate(dateString, timezone, formatOptions = {}) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const options = {
      ...formatOptions,
      timeZone: timezone,
    };
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
}

// Helper for formatting time only (e.g., 2:30 PM)
export function formatTime(dateString, timezone) {
  return formatDate(dateString, timezone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Helper for formatting date only (e.g., May 18, 2026)
export function formatDateOnly(dateString, timezone) {
  return formatDate(dateString, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Formats with date and time
export function formatDateTime(dateString, timezone) {
  return formatDate(dateString, timezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Formats relative time if it's today in the target timezone, otherwise formats date and time
export function formatDateTimeRelative(dateString, timezone) {
    if (!dateString) return "";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        const now = new Date();
        
        // Convert both to the target timezone's local date string for comparison
        const dateOptions = { timeZone: timezone, year: 'numeric', month: 'numeric', day: 'numeric' };
        const targetDateStr = new Intl.DateTimeFormat('en-US', dateOptions).format(date);
        const nowDateStr = new Intl.DateTimeFormat('en-US', dateOptions).format(now);
        
        if (targetDateStr === nowDateStr) {
            return `Today, ${formatTime(dateString, timezone)}`;
        }
        
        // Check for yesterday
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = new Intl.DateTimeFormat('en-US', dateOptions).format(yesterday);
        
        if (targetDateStr === yesterdayStr) {
            return `Yesterday, ${formatTime(dateString, timezone)}`;
        }
        
        return formatDateTime(dateString, timezone);
    } catch (e) {
        return dateString;
    }
}

// Checks if a scheduled date and time has passed (is overdue) in the organization's timezone
export function isFollowupOverdue(scheduledDate, scheduledTime, timezone) {
  if (!scheduledDate) return false;
  try {
    const tz = timezone || getLocalTimezone();
    const now = new Date();
    
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(now);
    const map = {};
    parts.forEach(p => { map[p.type] = p.value; });
    
    const currentLocalStr = `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`;
    const scheduledLocalStr = scheduledTime ? `${scheduledDate}T${scheduledTime}` : `${scheduledDate}T23:59:59`;
    
    return scheduledLocalStr < currentLocalStr;
  } catch (error) {
    console.error("Error in timezone comparison:", error);
    // Fallback to local browser comparison
    const now = new Date();
    const scheduledStr = scheduledTime ? `${scheduledDate}T${scheduledTime}` : `${scheduledDate}T23:59:59`;
    return new Date(scheduledStr) < now;
  }
}

// Returns 'Closed', 'Overdue', or 'Upcoming' in a timezone-aware manner
export function getTemporalStatus(f, timezone) {
  if (!f || f.status === "Closed") return "Closed";
  return isFollowupOverdue(f.scheduledDate, f.scheduledTime, timezone) ? "Overdue" : "Upcoming";
}
